import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    FormDateField,
    FormSelect,
    FormTextarea,
    FormTextField,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { useRelocateFishGroup } from '@/domain/fishGroups';

import { RelocateBioscopesSection } from './relocate/RelocateBioscopesSection';
import { buildRelocateRequest, type FormValues, makeDefaultValues } from './relocate/relocateForm';
import { useRelocateCages } from './relocate/useRelocateCages';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
    fishGroupName: string;
    currentCageId: number | null;
    currentLocation?: { cageName: string; locationName: string; fromDate: string } | null;
    isOpen: boolean;
    onClose: () => void;
}

export default function RelocateFishGroupModal(props: Props) {
    const {
        clientId,
        fishGroupId,
        fishGroupName,
        currentCageId,
        currentLocation,
        isOpen,
        onClose,
    } = props;
    const { t } = useTranslation('fishGroups');
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: makeDefaultValues() });
    const { emptyCages, sourceCage, sourceBioscopes, targetCage, hasBioscopeMovePermission } =
        useRelocateCages(clientId, currentCageId, control);

    // Bioscopes the operator can opt into moving alongside the FG. State is
    // local (not react-hook-form) because the checkbox list isn't a
    // conventional form input — toggling is a Set mutation, not a single
    // controlled value, and react-hook-form's overhead earns nothing here.
    const [selectedBioscopeIds, setSelectedBioscopeIds] = useState<Set<number>>(new Set());

    const handleClose = () => {
        if (isSubmitting) return;
        reset(makeDefaultValues());
        setSelectedBioscopeIds(new Set());
        onClose();
    };

    const {
        relocate,
        error: serverError,
        reset: resetMutation,
    } = useRelocateFishGroup(clientId, fishGroupId, {
        onSuccess: () => {
            ToastSuccess(t('actions.relocate.successToast'), 'fish-group-relocate-success');
            reset(makeDefaultValues());
            setSelectedBioscopeIds(new Set());
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    const toggleBioscope = (bioscopeId: number) => {
        setSelectedBioscopeIds((prev) => {
            const next = new Set(prev);
            if (next.has(bioscopeId)) next.delete(bioscopeId);
            else next.add(bioscopeId);
            return next;
        });
    };

    // Bounds the picker (day granularity) and the submitted timestamp (instant
    // granularity) — the backend requires a move strictly after it.
    const locationFromDate = currentLocation ? new Date(currentLocation.fromDate) : null;

    const onSubmit = async (values: FormValues) => {
        const requestBody = buildRelocateRequest(values, {
            selectedBioscopeIds,
            hasBioscopeMovePermission,
            locationFromDate,
        });
        if (!requestBody) return;
        await relocate(requestBody).catch(() => {});
    };

    const targetCageOptions = emptyCages.map((cage) => ({
        value: String(cage.cageId),
        label: `${cage.cageName} (${cage.locationName})`,
    }));

    const showBioscopesSection = sourceBioscopes.length > 0;
    const destinationCageName = targetCage?.cageName ?? '';

    const body = (
        <>
            <p className={styles.contextLine}>
                {t('actions.relocate.context')}{' '}
                <span className={styles.contextStrong}>{fishGroupName}</span>
                {currentLocation && (
                    <span className={styles.contextMeta}>
                        {' · '}
                        {currentLocation.cageName} ({currentLocation.locationName})
                    </span>
                )}
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormSelect
                    control={control}
                    name="targetCageId"
                    label={t('actions.relocate.fields.targetCage')}
                    placeholder={t('actions.relocate.fields.targetCagePlaceholder')}
                    options={targetCageOptions}
                    rules={{ required: true }}
                    note={emptyCages.length === 0 ? t('actions.relocate.noEmptyCages') : null}
                />
                {showBioscopesSection && (
                    <RelocateBioscopesSection
                        sourceCageName={currentLocation?.cageName ?? sourceCage?.cageName ?? ''}
                        destinationCageName={destinationCageName}
                        sourceBioscopes={sourceBioscopes}
                        targetBioscopes={targetCage?.activeBioscopes ?? null}
                        selectedBioscopeIds={selectedBioscopeIds}
                        onToggle={toggleBioscope}
                        canMove={hasBioscopeMovePermission}
                    />
                )}
                <FormDateField
                    control={control}
                    name="relocationDate"
                    label={t('actions.fields.relocationDate')}
                    rules={{ required: true }}
                    blockFutureDates
                    minDate={locationFromDate}
                />
                <FormTextField
                    control={control}
                    name="reason"
                    label={t('actions.fields.reason')}
                    optional
                />
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button
                variant="primary"
                type="submit"
                form={formId}
                disabled={isSubmitting || emptyCages.length === 0}>
                {t('actions.relocate.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.relocate.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
